import { Box, IconButton, Tooltip, useToast } from '@chakra-ui/react';
import type {
  VoucherEligibilityItem,
  VoucherGetByCodeResponse,
} from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import { getItemCardTrackingParams, trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import {
  getProductDetailOptions,
  useAuthStore,
  useGetProductDetail,
  useInvalidateVoucherList,
  useShipToStore,
} from '@diamond/sol-buyer-context';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { AddToCartPopup } from '../../common/add-to-cart-popup/add-to-cart-popup';
import { AddToCartIcon } from '../../voucher/icons';
import {
  type VoucherRuleProductAction,
  mapVoucherRuleProducts,
} from '../../voucher/rule-product-utils';

type VoucherDetailsItem = VoucherGetByCodeResponse | VoucherEligibilityItem;

type SelectedProductInfo = {
  productId: string;
  defaultQuantity?: number | null;
};

interface VoucherDetailAddToCartShortcutProps {
  voucher: VoucherDetailsItem;
  isProcessing: boolean;
}

export const VoucherDetailAddToCartShortcut = ({
  voucher,
  isProcessing,
}: VoucherDetailAddToCartShortcutProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const token = useAuthStore((state) => state.access_token);
  const selectedShipToId = useShipToStore((state) => state.selectedShipToId);
  const { invalidate: invalidateVoucherList } = useInvalidateVoucherList();
  const [isAddToCartOpen, setIsAddToCartOpen] = useState(false);
  const [selectedProductInfo, setSelectedProductInfo] =
    useState<SelectedProductInfo | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );
  const { data: selectedProductDetail } = useGetProductDetail(
    selectedProductId ?? undefined,
    selectedShipToId ?? undefined
  );

  const ruleProductGroups = mapVoucherRuleProducts(voucher);
  const primaryRuleProduct = ruleProductGroups[0]?.products[0];
  const canShortcutAdd =
    Boolean(token) &&
    Boolean(selectedShipToId) &&
    Boolean(primaryRuleProduct) &&
    !primaryRuleProduct?.isOutOfStock;

  const handleCloseAddToCart = () => {
    setIsAddToCartOpen(false);
    setSelectedProductInfo(null);
    setSelectedProductId(null);
  };

  const handleAddToCartSuccess = () => {
    void invalidateVoucherList();
    showToast(
      toast,
      'success',
      'Berhasil menambahkan produk ke keranjang',
      undefined,
      'bottom',
      3000
    );
    handleCloseAddToCart();
  };

  const handleRuleProductAdd = async (product: VoucherRuleProductAction) => {
    if (product.isOutOfStock || !token || !selectedShipToId) {
      return;
    }

    const quantity =
      product.missingQuantity > 0
        ? product.missingQuantity
        : product.minOrderQuantity ?? 1;

    const productDetail = await queryClient.fetchQuery(
      getProductDetailOptions(token, product.productId, selectedShipToId)
    );

    trackEvent(TRACKING_EVENTS.PRE_ADD_TO_CART, {
      ...getItemCardTrackingParams(
        productDetail,
        quantity,
        TRACKING_PAGES.VOUCHER_DETAIL,
        TRACKING_PAGES.VOUCHER_DETAIL
      ),
      source: 'voucher-detail-shortcut',
    });

    setSelectedProductId(product.productId);
    setSelectedProductInfo({
      productId: product.productId,
      defaultQuantity:
        product.normalizedMissingQuantity > 0
          ? product.normalizedMissingQuantity
          : product.minOrderQuantity ?? null,
    });
    setIsAddToCartOpen(true);
  };

  const handleShortcutAddToCart = async () => {
    if (!primaryRuleProduct) {
      showToast(
        toast,
        'info',
        'Produk terkait voucher belum tersedia',
        undefined,
        'bottom',
        3000
      );
      return;
    }

    await handleRuleProductAdd(primaryRuleProduct);
  };

  return (
    <>
      <Tooltip
        label="Tambahkan produk terkait voucher"
        placement="top"
        hasArrow
        shouldWrapChildren
      >
        <Box as="span" pointerEvents="auto">
          <IconButton
            aria-label="Tambahkan produk voucher ke keranjang"
            icon={<AddToCartIcon color="#0000A3" boxSize="16px" />}
            size="sm"
            variant="ghost"
            minW="auto"
            h="32px"
            w="32px"
            p="0"
            bg="#F2F2F7"
            borderRadius="6px"
            _hover={{ bg: '#E9E9EF' }}
            onClick={(event) => {
              event.stopPropagation();
              void handleShortcutAddToCart();
            }}
            isDisabled={Boolean(isProcessing) || !canShortcutAdd}
          />
        </Box>
      </Tooltip>
      {selectedProductInfo && token && selectedShipToId && isAddToCartOpen && (
        <AddToCartPopup
          isOpen={isAddToCartOpen}
          onClose={handleCloseAddToCart}
          product={selectedProductDetail}
          productId={selectedProductInfo.productId}
          token={token}
          selectedShipToId={selectedShipToId}
          section="Voucher Details"
          page="Voucher Details"
          defaultQuantity={selectedProductInfo.defaultQuantity ?? undefined}
          autoSelectProductId={selectedProductInfo.productId}
          onSuccess={handleAddToCartSuccess}
          enableCartStoreSync={true}
          shouldAutoSelectOnAdd={true}
        />
      )}
    </>
  );
};

export default VoucherDetailAddToCartShortcut;
